import type { MarkingPage, StoredMarking } from '#shared/utils/markings/schema'
import type { Marking } from '~/utils/markings'
import {
  markingCreateSchema,
  storedMarkingSchema,
} from '#shared/utils/markings/schema'
import { isFetchError } from '#shared/utils/error-handling'
import { datadogRum } from '@datadog/browser-rum'
import { MARKING_MESSAGES } from '~/constants/markings'
import { parse } from 'valibot'
import { loadAllMarkings } from '~/utils/markings/client'
import { getToast } from '~/utils/toast'
import {
  getCurrentPageUrl,
  normalizeMarkingPageUrl,
  workKeyForMarking,
} from '~/utils/markings'

type EditableMarking = Partial<Pick<Marking, 'color' | 'note'>>

interface MarkingRequest {
  userId: string
  id: string
  signal: AbortSignal
}

function markingsQueryKey(userId?: string) {
  return [userId, 'markings'] as const
}

function _useMarkings() {
  const { user, loggedIn } = useUserSession()
  const queryClient = useQueryClient()
  const queryKey = computed(() => markingsQueryKey(user.value?.userId))
  // Deliberately no expiresAt check: that timestamp tracks the Core access
  // token, which only refreshes during SSR, so it goes stale in a long-lived
  // tab. Markings never call Core — they need the session cookie, which the
  // server validates. A genuinely lost session surfaces as a 401 instead.
  const isAuthenticated = computed(
    () => loggedIn.value && !!user.value?.userId,
  )
  const activeRequests = new Map<string, AbortController>()
  const toast = getToast()

  const query = useQuery({
    queryKey,
    enabled: computed(() => import.meta.client && isAuthenticated.value),
    retry: false,
    // A refetch walks every page of the collection, and writes already put the
    // confirmed record into the cache, so staleness never gates one on focus or
    // mount. Coming back from offline is the one case where the cache can have
    // missed a write, so that refetch is forced past the stale check.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnReconnect: 'always',
    queryFn: async ({ signal, queryKey: [owner] }) => {
      return loadAllMarkings(async (cursor) => {
        if (owner !== user.value?.userId) {
          throw new Error('Markings owner changed')
        }

        return $fetch<MarkingPage>('/api/markings', { query: { cursor }, signal, retry: 0 })
      })
    },
  })
  const allMarkings = computed<Marking[]>(() =>
    (query.data.value ?? []).map((record) => record.data),
  )

  async function prepareWrite(request: MarkingRequest) {
    await queryClient.cancelQueries({ queryKey: markingsQueryKey(request.userId) })

    if (request.signal.aborted || request.userId !== user.value?.userId || !isAuthenticated.value) {
      throw new Error('Markings account changed')
    }
  }

  function updateCache(record: StoredMarking | null, request: MarkingRequest) {
    if (request.signal.aborted || request.userId !== user.value?.userId) {
      return
    }

    queryClient.setQueryData<StoredMarking[]>(markingsQueryKey(request.userId), (records = []) => {
      if (!record) {
        return records.filter(marking => marking.data.id !== request.id)
      }

      const index = records.findIndex(marking => marking.data.id === request.id)

      if (index === -1) {
        return [...records, record]
      }

      return records.map((existing, position) => position === index ? record : existing)
    })
  }

  async function handleWriteError(error: Error, request: MarkingRequest) {
    if (request.signal.aborted || request.userId !== user.value?.userId) {
      return
    }

    const conflict = isFetchError(error, 409)
    datadogRum.addError(new Error('Marking write could not be confirmed'), { conflict })
    toast.error(conflict ? MARKING_MESSAGES.writeConflict : MARKING_MESSAGES.writeFailed)

    if (conflict) {
      await queryClient.invalidateQueries({ queryKey: markingsQueryKey(request.userId) })
    }
  }

  // The record as it stood before an optimistic write, so a refused one can put
  // it back. Keyed by id alone: `activeRequests` holds writes to one marking to
  // a single in-flight request, and `save` drops the entry once it settles.
  const snapshots = new Map<string, StoredMarking>()

  function takeSnapshot(request: MarkingRequest) {
    const record = queryClient.getQueryData<StoredMarking[]>(markingsQueryKey(request.userId))
      ?.find(marking => marking.data.id === request.id)

    if (!record) {
      throw new Error('Marking not loaded')
    }

    snapshots.set(request.id, record)
    return record
  }

  function snapshotFor(request: MarkingRequest) {
    const record = snapshots.get(request.id)

    if (!record) {
      throw new Error('Marking not loaded')
    }

    return record
  }

  async function rollback(error: Error, request: MarkingRequest) {
    const snapshot = snapshots.get(request.id)

    if (snapshot) {
      updateCache(snapshot, request)
    }

    await handleWriteError(error, request)
  }

  // Rehydration unwraps any <mark> whose marking is absent from the cache, so
  // the optimistic wrap in useSelectionMarkings only survives the round trip
  // when the store carries the marking too. Mirrors the record the server
  // writes for a new marking.
  async function prepareCreate(request: MarkingRequest & { marking: Marking }) {
    await prepareWrite(request)
    updateCache(
      { data: request.marking, recordVersion: 1, updatedAt: request.marking.createdAt },
      request,
    )
  }

  async function rollbackCreate(error: Error, request: MarkingRequest) {
    updateCache(null, request)
    await handleWriteError(error, request)
  }

  const mutationOptions = {
    retry: false,
    onMutate: prepareWrite,
    onSuccess: updateCache,
    onError: handleWriteError,
  }

  // Swatches and the note render from the store, so waiting for the round trip
  // to update it showed every click as a lag. `recordVersion` is carried over
  // untouched: it is what the PATCH sends as `expectedVersion`, and only the
  // server assigns the next one.
  async function prepareUpdate(request: MarkingRequest & { patch: EditableMarking }) {
    await prepareWrite(request)
    const previous = takeSnapshot(request)
    updateCache({ ...previous, data: { ...previous.data, ...request.patch } }, request)
  }

  async function prepareDelete(request: MarkingRequest) {
    await prepareWrite(request)
    takeSnapshot(request)
    updateCache(null, request)
  }

  const createMutation = useMutation({
    ...mutationOptions,
    onMutate: prepareCreate,
    onError: rollbackCreate,
    mutationKey: ['markings', 'create'],
    async mutationFn(request: MarkingRequest & { marking: Marking }) {
      const { createdAt: _createdAt, ...input } = request.marking
      const response = await $fetch<StoredMarking>('/api/markings', {
        method: 'POST',
        signal: request.signal,
        body: parse(markingCreateSchema, input),
        retry: 1,
      })
      return parse(storedMarkingSchema, response)
    },
  })

  const updateMutation = useMutation({
    ...mutationOptions,
    onMutate: prepareUpdate,
    onError: rollback,
    mutationKey: ['markings', 'update'],
    async mutationFn(request: MarkingRequest & { patch: EditableMarking }) {
      const existing = snapshotFor(request)
      const url = `/api/markings/${encodeURIComponent(request.id)}`
      const response = await $fetch<StoredMarking>(url, {
        method: 'PATCH',
        signal: request.signal,
        body: { ...request.patch, expectedVersion: existing.recordVersion },
        retry: 0,
      })
      return parse(storedMarkingSchema, response)
    },
  })

  const deleteMutation = useMutation({
    ...mutationOptions,
    onMutate: prepareDelete,
    onError: rollback,
    mutationKey: ['markings', 'delete'],
    async mutationFn(request: MarkingRequest) {
      const existing = snapshotFor(request)
      const url = `/api/markings/${encodeURIComponent(request.id)}`
      await $fetch(url, {
        method: 'DELETE',
        signal: request.signal,
        body: { expectedVersion: existing.recordVersion },
        retry: 0,
      })
      return null
    },
  })

  async function save(id: string, mutate: (request: MarkingRequest) => Promise<StoredMarking | null>) {
    const userId = user.value?.userId

    if (!userId || !isAuthenticated.value) {
      // Reaching a marking write with no session at all is anomalous, not a
      // routine expiry, and the user only sees a toast telling them to reload.
      datadogRum.addError(new Error('Marking write attempted without a session'))
      toast.error(MARKING_MESSAGES.sessionExpired)
      return false
    }

    if (activeRequests.has(id)) {
      return false
    }

    const controller = new AbortController()
    activeRequests.set(id, controller)

    try {
      if (!query.isSuccess.value) {
        const result = await query.refetch()

        if (controller.signal.aborted || userId !== user.value?.userId) {
          return false
        }

        if (!result.isSuccess) {
          toast.error(MARKING_MESSAGES.loadFailed)
          return false
        }
      }

      await mutate({ userId, id, signal: controller.signal })
      return !controller.signal.aborted && userId === user.value?.userId
    }
    catch {
      // The mutation callbacks report the error and put the store back. Callers
      // change the DOM before this resolves, so `false` is their cue to restore
      // it from the store, along with any draft they hold.
      return false
    }
    finally {
      if (activeRequests.get(id) === controller) {
        activeRequests.delete(id)
        snapshots.delete(id)
      }
    }
  }

  function addMarking(marking: Marking) {
    return save(marking.id, request => createMutation.mutateAsync({ ...request, marking }))
  }

  function updateMarking(id: string, patch: EditableMarking) {
    return save(id, request => updateMutation.mutateAsync({ ...request, patch }))
  }

  function removeMarking(id: string) {
    return save(id, request => deleteMutation.mutateAsync(request))
  }

  const placement = ref<Record<string, 'placed' | 'unplaced'>>({})
  watch(
    () => user.value?.userId,
    () => {
      for (const controller of activeRequests.values()) {
        controller.abort()
      }

      activeRequests.clear()
      snapshots.clear()
      placement.value = {}
    },
    { flush: 'sync' },
  )

  return {
    allMarkings,
    addMarking,
    updateMarking,
    removeMarking,
    isReady: query.isSuccess,
    isLoading: query.isLoading,
    isError: query.isError,
    refresh: query.refetch,
    placement,
    getCurrentPageUrl,
    markingsForPage: (pageUrl: string) => {
      const target = normalizeMarkingPageUrl(pageUrl)
      return allMarkings.value.filter(
        (marking) => normalizeMarkingPageUrl(marking.pageUrl) === target,
      )
    },
    markingsForWork: (workKey: string) =>
      allMarkings.value.filter((marking) => workKeyForMarking(marking) === workKey),
  }
}

export const useMarkings = createSharedComposable(_useMarkings)
